import React, { useState } from "react";
import { Button, Skeleton, useToast } from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import { useSignedInUser } from "../../auth/hooks/useSignedInUser";
import {
  availableTo,
  MEETING_APPS,
  type MeetingApp,
  type MeetingAppId,
} from "../meetingApps";
import { useMeetingApps } from "../hooks/useMeetingApps";
import MeetingAppMark from "./MeetingAppMark";

/**
 * The meeting apps this practice can hand its online appointments to, and
 * whether each one is on — My brand → Configuration, under the dish search
 * setting.
 *
 * Under My brand rather than Settings for the same reason the setting above
 * it is: it is a choice about how this practice works, not about the account.
 *
 * One row per app, and every app gets a row whether or not the backend knows
 * it yet — see `MEETING_APPS`. The rows come from that list rather than from
 * the server's `providers`, because the list is also a statement of what is
 * on the way, and an app that appeared from nowhere in a later release would
 * read as one nobody was told about. What the server says is merged onto the
 * row it names, by slug.
 *
 * Connect does one of two things, and which one is not this card's to decide
 * — see `useMeetingApps`. With no account on file for that app it hands back
 * a sign-in URL and the browser leaves; with one already linked it flips the
 * setting where it stands. Disconnect never signs anything out: it puts the
 * workspace back to making links by hand, and the account stays linked, so
 * turning it on again costs one press rather than another trip out.
 *
 * Only one app makes the links, because `meeting_provider` is one field on
 * the workspace. So connecting the second app is also what disconnects the
 * first, in one press and with both accounts kept — which the toast says, so
 * that the other row going quiet is something somebody was told about rather
 * than something they notice afterwards.
 */

/**
 * The shape both rows below are laid out in — the real one and its skeleton —
 * so the two cannot drift apart.
 *
 * Two layouts, and the width of a phone is the whole of the difference. On a
 * desktop a row is one line: the mark and the words on the left, the button
 * on the right. A phone has about 320px inside this card, and that one line
 * has to hold a 32px mark, a name, a state and a button. What gave way was
 * the description — it wrapped to three lines in a column narrower than the
 * button standing beside it, and the row stopped reading as a row.
 *
 * So on a phone the row is a grid of two lines rather than one line. The top
 * line keeps the mark, the name and the button — which app this is, and what
 * it can be told to do, which is what somebody opens this card for — and the
 * description drops to a line of its own under the name, running out to the
 * card's edge. Which is the shape a phone's own settings screens have, and
 * the reason every app on a phone draws its integrations this way.
 *
 * The grid placements the rows below carry are unprefixed on purpose: from
 * `md` up the row is a flex line, and `grid-column`/`grid-row` do nothing to
 * a flex item, so the phone's layout needs no `max-md:` and the desktop's
 * needs no undoing.
 */
const ROW =
  "grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 " +
  // A hairline between apps, and only on a phone. On a desktop the air
  // between the rows is enough to tell them apart — which is what the `ul`
  // below says. Two lines each in a narrow column is not, and without a rule
  // the two apps read as one block of text.
  "border-t border-line pt-4 first:border-t-0 first:pt-0 " +
  "md:flex md:items-center md:justify-between md:gap-4 md:border-t-0 md:pt-0";

/** One app's row. Split out so the states below read as one shape. */
const AppRow: React.FC<{
  app: MeetingApp;
  /**
   * Whether there is anything to press on this row — shipped to everybody,
   * or still in its pilot and this is one of the accounts running it. Passed
   * in rather than read off `app`, because the answer depends on who is
   * signed in and a row is not the thing that knows that.
   */
  available: boolean;
  /** Whether this app is the one making the links. */
  enabled: boolean;
  /** Whose account is on file, where the backend named one. */
  accountEmail: string | null;
  /** Set while this row's own press is with the server. */
  busy: boolean;
  /** Set while another row's press is, which holds this one. */
  held: boolean;
  onConnect: () => void;
  onDisconnect: () => void;
}> = ({
  app,
  available,
  enabled,
  accountEmail,
  busy,
  held,
  onConnect,
  onDisconnect,
}) => (
  <li className={ROW}>
    {/* The two wrappers that group the left side on a desktop are
        `display: contents` on a phone, so the mark, the name and the
        description are the grid's own cells rather than one cell holding all
        three. One DOM for both layouts — and in particular one button, since
        a second copy hidden at the other size is a second thing to keep
        spinning, disabling and labelling. */}
    <div className="contents md:flex md:min-w-0 md:items-start md:gap-3">
      <MeetingAppMark
        app={app.id}
        size={32}
        className="col-start-1 row-start-1 md:mt-0.5"
      />
      <div className="contents md:block md:min-w-0">
        <div className="col-start-2 row-start-1 flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="text-[15px] 3xl:text-[16px] font-medium text-ink">
            {app.name}
          </span>
          {/* Beside the name rather than where the button is: what is being
              said is which app this is — a connected one — and a state read
              from the far end of the row is one nobody ties back to it. */}
          {enabled && (
            <span className="flex items-center gap-1.5 text-[12px] 3xl:text-[13px] font-medium text-[#16A34A]">
              <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-[#16A34A]" />
              Connected
            </span>
          )}
        </div>
        {/* On a phone this is the second line, starting under the name and
            running past the button above it. `break-words` because an address
            is one word as far as a browser is concerned, and a long one would
            otherwise push the columns it spans wider than the card. */}
        <p className="col-span-2 col-start-2 row-start-2 mt-1.5 break-words text-[13px] 3xl:text-[14px] text-ink-muted md:mt-0.5">
          {/* Whose account it is, once there is one, in place of the sales
              line: somebody who has connected it has read that line already,
              and which Google account this went to is the thing they come
              back to the card to check. */}
          {enabled && accountEmail ? accountEmail : app.description}
        </p>
      </div>
    </div>

    <div className="col-start-3 row-start-1 flex-shrink-0 justify-self-end">
      {!available ? (
        // Not a button, and not a disabled one either: there is nothing to
        // press and a greyed-out Connect would read as something this
        // workspace is not allowed to do rather than something that does not
        // exist yet.
        //
        // The one blue in the card, and not the brand's teal: it is a note
        // about the app beside it rather than something of ours to press, and
        // the teal everything pressable here wears would say it was.
        <span className="text-[13px] 3xl:text-[14px] font-medium text-[#007AFF]">
          Coming soon
        </span>
      ) : enabled ? (
        // Quiet, against the filled Connect it replaces. Turning an
        // integration off is a thing somebody goes looking for, not the thing
        // the row is offering, and a second filled button on the card would
        // compete with the Connect on the row under it.
        <Button
          variant="quiet"
          size="sm"
          loading={busy}
          disabled={held}
          onClick={onDisconnect}
        >
          Disconnect
        </Button>
      ) : (
        <Button size="sm" loading={busy} disabled={held} onClick={onConnect}>
          Connect
        </Button>
      )}
    </div>
  </li>
);

/** The rows' shape while the settings are still being read. */
const AppRowSkeleton: React.FC = () => (
  <li className={ROW}>
    <div className="contents md:flex md:min-w-0 md:flex-1 md:items-start md:gap-3">
      <Skeleton className="col-start-1 row-start-1 h-8 w-8 flex-shrink-0 rounded-md" />
      <div className="contents md:block md:min-w-0 md:flex-1">
        <Skeleton className="col-start-2 row-start-1 h-4 w-32" />
        <Skeleton className="col-span-2 col-start-2 row-start-2 mt-2 h-3 w-full md:max-w-[420px]" />
      </div>
    </div>
    <Skeleton className="col-start-3 row-start-1 h-8 w-24 flex-shrink-0 justify-self-end rounded-md" />
  </li>
);

const IntegrateAppsCard: React.FC = () => {
  const toast = useToast();
  const { loading, isEnabled, accountEmail, connect, disconnect } = useMeetingApps();
  // Who is reading the card, for the one row that is still in a pilot — see
  // `availableTo`. `isLoaded` is held with the settings' own wait below:
  // until Clerk has restored the session there is no address to match, and a
  // row drawn on that would say "Coming soon" to a pilot account for a frame
  // and then take it back.
  const { email, isLoaded } = useSignedInUser();

  // Which row is with the server, so its own button spins and the other is
  // held: two providers changed at once is a race the backend would settle
  // arbitrarily, and the card would then be drawing the loser.
  const [busy, setBusy] = useState<MeetingAppId | null>(null);

  const say = (what: string, reason: unknown) => {
    toast.error(
      what,
      reason instanceof ApiError
        ? reason.message
        : "We could not reach the server. Check your connection and try again."
    );
  };

  const handleConnect = async (app: MeetingApp) => {
    // Which app this one is about to displace, read before the write: the
    // setting holds one provider, so turning this on turns that off, and
    // afterwards there is nothing left on the settings to say what it was.
    const displaced = MEETING_APPS.find(
      (other) => other.id !== app.id && isEnabled(other.id)
    );

    setBusy(app.id);
    try {
      const signInUrl = await connect(app.id);
      if (signInUrl) {
        // Off to Google. `assign` rather than `replace`, so the back button
        // still comes home if somebody changes their mind on Google's screen.
        // The spinner is left running on purpose: this page is on its way
        // out, and a button that went back to saying "Connect" while the
        // browser was already leaving would read as a press that failed.
        window.location.assign(signInUrl);
        return;
      }
      // Already linked — the setting was flipped where it stood, with no
      // trip out of the app.
      toast.success(
        `${app.name} connected`,
        displaced
          ? `New online appointments will get a ${app.name} link. ${displaced.name} has been switched off.`
          : "New online appointments will get a meeting link."
      );
      setBusy(null);
    } catch (reason) {
      say(`Could not connect ${app.name}`, reason);
      setBusy(null);
    }
  };

  const handleDisconnect = async (app: MeetingApp) => {
    setBusy(app.id);
    try {
      await disconnect(app.id);
      toast.success(
        `${app.name} disconnected`,
        "Online appointments will no longer get a meeting link."
      );
    } catch (reason) {
      say(`Could not disconnect ${app.name}`, reason);
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="card-surface border border-line bg-white px-5 py-4 3xl:px-6 3xl:py-5">
      <h2 className="text-[15px] 3xl:text-[16px] font-medium text-ink">
        Integrate apps
      </h2>

      {/* A list, because that is what it is: the rows are the same thing said
          about different apps. On a desktop the gap between them is wide
          enough that they need no rules to be told apart; on a phone, where
          each row is two lines, the rule that separates them is part of
          `ROW`. */}
      <ul className="mt-4 flex flex-col gap-4 md:gap-5">
        {loading || !isLoaded
          ? MEETING_APPS.map((app) => <AppRowSkeleton key={app.id} />)
          : MEETING_APPS.map((app) => (
              <AppRow
                key={app.id}
                app={app}
                available={availableTo(app, email)}
                enabled={isEnabled(app.id)}
                accountEmail={accountEmail(app.id)}
                busy={busy === app.id}
                held={busy !== null && busy !== app.id}
                onConnect={() => handleConnect(app)}
                onDisconnect={() => handleDisconnect(app)}
              />
            ))}
      </ul>
    </section>
  );
};

export default IntegrateAppsCard;
